import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search as SearchIcon } from "lucide-react";
import { SongCard, AlbumCard, ArtistCard } from "@/components/MusicCard";
import TrackRow from "@/components/TrackRow";
import Section from "@/components/Section";

const GENRES = [
  { name: "Pop", color: "from-[#FF2E00] to-[#FF2E00]", q: "pop hits" },
  { name: "Hip-Hop", color: "from-[#E2FF31] to-[#FF2E00]", q: "hip hop" },
  { name: "Rock", color: "from-[#FF2E00] to-[#1FB6FF]", q: "rock anthems" },
  { name: "Electronic", color: "from-[#1FB6FF] to-[#E2FF31]", q: "electronic dance" },
  { name: "R&B", color: "from-[#FF2E00] to-[#E2FF31]", q: "rnb essentials" },
  { name: "Indie", color: "from-[#FF2E00] to-[#FF2E00]", q: "indie favourites" },
  { name: "Jazz", color: "from-[#E2FF31] to-[#1FB6FF]", q: "jazz classics" },
  { name: "Classical", color: "from-[#1FB6FF] to-[#FF2E00]", q: "classical masterpieces" },
  { name: "Country", color: "from-[#FF2E00] to-[#1FB6FF]", q: "country hits" },
  { name: "Latin", color: "from-[#E2FF31] to-[#FF2E00]", q: "latin hits" },
  { name: "K-Pop", color: "from-[#FF2E00] to-[#E2FF31]", q: "k-pop hits" },
  { name: "Metal", color: "from-[#1FB6FF] to-[#FF2E00]", q: "metal anthems" },
];

const Search = () => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (term) => {
    if (!term?.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/search", { params: { q: term, limit: 30 } });
      const all = r.data.results || [];
      setResults({
        songs: all.filter((x) => x.wrapperType === "track" && x.kind === "song"),
        albums: all.filter((x) => x.wrapperType === "collection"),
        artists: all.filter((x) => x.wrapperType === "artist"),
      });
    } catch {
      setResults({ songs: [], albums: [], artists: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  // React to ?q= URL changes (back/forward + deep links from other pages)
  useEffect(() => {
    const urlQ = params.get("q") || "";
    if (urlQ !== q) setQ(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const t = setTimeout(() => {
      doSearch(q);
      // Keep the URL in sync so /search?q=Drake is shareable + back-navigable
      const cur = params.get("q") || "";
      if (q.trim() && cur !== q) setParams({ q }, { replace: true });
      else if (!q.trim() && cur) setParams({}, { replace: true });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, doSearch]);

  return (
    <div data-testid="search-page" className="pb-12">
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 py-5 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/5 mb-8">
        <div className="relative max-w-2xl">
          <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search songs, albums, artists..."
            className="w-full bg-white/5 border border-white/10 focus:border-[#FF2E00] outline-none rounded-full pl-11 pr-5 py-3 text-base placeholder-white/30 transition"
          />
        </div>
      </div>

      {!q && (
        <Section title="Browse all" testId="section-genres">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {GENRES.map((g) => (
              <button
                key={g.name}
                onClick={() => setQ(g.q)}
                data-testid={`genre-${g.q}`}
                className={`relative aspect-[16/10] rounded-xl bg-gradient-to-br ${g.color} p-5 text-left overflow-hidden hover:scale-[1.03] transition-transform shadow-lg`}
              >
                <span className="font-display text-2xl font-semibold text-black/90">{g.name}</span>
                <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-lg rotate-12 bg-black/30 blur-sm" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {q && loading && (
        <p className="text-white/40 text-sm">Searching...</p>
      )}

      {q && !loading && results && (
        <>
          {results.songs.length > 0 && (
            <Section title="Songs" testId="search-songs">
              <div className="flex flex-col">
                {results.songs.slice(0, 10).map((s, i) => (
                  <TrackRow key={s.trackId} track={s} index={i} queue={results.songs} />
                ))}
              </div>
            </Section>
          )}
          {results.artists.length > 0 && (
            <Section title="Artists" testId="search-artists">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {results.artists.slice(0, 6).map((a) => (
                  <ArtistCard key={a.artistId} artist={a} />
                ))}
              </div>
            </Section>
          )}
          {results.albums.length > 0 && (
            <Section title="Albums" testId="search-albums">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {results.albums.slice(0, 12).map((a) => (
                  <AlbumCard key={a.collectionId} album={a} />
                ))}
              </div>
            </Section>
          )}
          {results.songs.length === 0 && results.albums.length === 0 && results.artists.length === 0 && (
            <p className="text-white/40">No results for &ldquo;{q}&rdquo;</p>
          )}
        </>
      )}
    </div>
  );
};

export default Search;
